import { useState } from 'react'
import {
  Card, Row, Col, Table, Button, Select, DatePicker, Space, Typography,
  Divider, message,
} from 'antd'
import { PrinterOutlined, SearchOutlined } from '@ant-design/icons'
import { useQuery } from '@tanstack/react-query'
import dayjs, { Dayjs } from 'dayjs'
import apiClient from '@/shared/api/client'
import { usePlantId } from '@/shared/hooks/usePlantId'
import PageHeader from '@/shared/components/PageHeader'

const { Text } = Typography

const fmtAmt = (v: number) =>
  Number(v ?? 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

interface PLLineItem {
  account: string
  group_type: string
  amount: number
}

function buildTableData(items: PLLineItem[]) {
  return items.map((r, i) => ({ ...r, key: i }))
}

function subtotal(items: PLLineItem[]) {
  return items.reduce((acc, r) => acc + (r.amount ?? 0), 0)
}

const lineColumns = [
  {
    title: 'Account Name',
    dataIndex: 'account',
    key: 'account',
    render: (v: string) => <Text style={{ fontSize: 13 }}>{v}</Text>,
  },
  {
    title: 'Amount (₹)',
    dataIndex: 'amount',
    key: 'amount',
    align: 'right' as const,
    width: 160,
    render: (v: number) => (
      <Text style={{ fontFamily: 'monospace', fontSize: 13 }}>
        {fmtAmt(v)}
      </Text>
    ),
  },
]

export default function ProfitLossPage() {
  const { plantId, plants, setPlantId } = usePlantId()
  const [fromDate, setFromDate] = useState<Dayjs>(dayjs().startOf('month'))
  const [toDate, setToDate] = useState<Dayjs>(dayjs())
  const [enabled, setEnabled] = useState(false)

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['mis-pl', plantId, fromDate?.format('YYYY-MM-DD'), toDate?.format('YYYY-MM-DD')],
    queryFn: () =>
      apiClient.get('/mis/profit-loss', {
        params: {
          plant_id: plantId,
          from_date: fromDate?.format('YYYY-MM-DD'),
          to_date: toDate?.format('YYYY-MM-DD'),
        },
      }).then(r => r.data),
    enabled: enabled && !!plantId,
  })

  const handleGenerate = () => {
    if (!plantId) {
      message.warning('Please select a plant')
      return
    }
    if (!fromDate || !toDate) {
      message.warning('Please select date range')
      return
    }
    setEnabled(true)
    refetch()
  }

  const handleExport = () => {
    message.info('Export coming soon')
  }

  const revenue: PLLineItem[] = data?.revenue ?? []
  const expenses: PLLineItem[] = data?.expenses ?? []
  const grossProfit: number = data?.gross_profit ?? 0
  const netProfit: number = data?.net_profit ?? 0

  const revenueSubtotal = subtotal(revenue)
  const expensesSubtotal = subtotal(expenses)

  const summaryStyle = (val: number) => ({
    color: val >= 0 ? '#52c41a' : '#ff4d4f',
    fontWeight: 700,
    fontSize: 16,
  })

  return (
    <div style={{ padding: 24 }}>
      <PageHeader
        title="Profit & Loss Statement"
        subtitle="Revenue and expense summary for the selected period"
        extra={
          <Button icon={<PrinterOutlined />} onClick={handleExport}>
            Print / Export
          </Button>
        }
      />

      {/* Filters */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Space wrap>
          <Select
            style={{ width: 200 }}
            placeholder="Select Plant"
            value={plantId || undefined}
            onChange={v => { setPlantId(v); setEnabled(false) }}
            options={plants.map((p: any) => ({ label: p.name, value: p.id }))}
          />
          <DatePicker
            placeholder="From Date"
            value={fromDate}
            onChange={v => { setFromDate(v!); setEnabled(false) }}
            format="DD-MMM-YYYY"
          />
          <DatePicker
            placeholder="To Date"
            value={toDate}
            onChange={v => { setToDate(v!); setEnabled(false) }}
            format="DD-MMM-YYYY"
          />
          <Button
            type="primary"
            icon={<SearchOutlined />}
            loading={isLoading}
            onClick={handleGenerate}
          >
            Generate
          </Button>
        </Space>
      </Card>

      {/* P&L Tables */}
      <Row gutter={16}>
        {/* Revenue */}
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={
              <Text strong style={{ color: '#52c41a', fontSize: 14 }}>
                REVENUE
              </Text>
            }
          >
            <Table
              dataSource={buildTableData(revenue)}
              columns={lineColumns}
              pagination={false}
              size="small"
              loading={isLoading}
              locale={{ emptyText: 'No revenue data' }}
              summary={() => (
                <Table.Summary.Row style={{ background: '#f6ffed' }}>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Total Revenue</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong style={{ fontFamily: 'monospace', color: '#52c41a' }}>
                      {fmtAmt(revenueSubtotal)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        </Col>

        {/* Expenses */}
        <Col xs={24} lg={12}>
          <Card
            size="small"
            title={
              <Text strong style={{ color: '#ff4d4f', fontSize: 14 }}>
                EXPENSES
              </Text>
            }
          >
            <Table
              dataSource={buildTableData(expenses)}
              columns={lineColumns}
              pagination={false}
              size="small"
              loading={isLoading}
              locale={{ emptyText: 'No expense data' }}
              summary={() => (
                <Table.Summary.Row style={{ background: '#fff2f0' }}>
                  <Table.Summary.Cell index={0}>
                    <Text strong>Total Expenses</Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1} align="right">
                    <Text strong style={{ fontFamily: 'monospace', color: '#ff4d4f' }}>
                      {fmtAmt(expensesSubtotal)}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}
            />
          </Card>
        </Col>
      </Row>

      {/* Summary bar */}
      {data && (
        <Card size="small" style={{ marginTop: 16, background: '#fafafa' }}>
          <Row gutter={16} justify="center">
            <Col xs={24} sm={12} style={{ textAlign: 'center', padding: '8px 0' }}>
              <Divider type="vertical" style={{ height: 'auto' }} />
              <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Gross Profit</Text>
              <Text style={summaryStyle(grossProfit)}>
                ₹ {fmtAmt(grossProfit)}
              </Text>
            </Col>
            <Col xs={24} sm={12} style={{ textAlign: 'center', padding: '8px 0' }}>
              <Text type="secondary" style={{ display: 'block', marginBottom: 4 }}>Net Profit</Text>
              <Text style={summaryStyle(netProfit)}>
                ₹ {fmtAmt(netProfit)}
              </Text>
            </Col>
          </Row>
        </Card>
      )}
    </div>
  )
}
